// ============================================================================
// uxComponents.js — Composants UX partagés (Toast, ConfirmModal, StatusBadge,
// GlossaryTooltip, EmptyState).
// ============================================================================
// Objectif : remplacer les alerts(), window.confirm(), badges ad-hoc et
// icônes-sans-tooltip par des composants réutilisables, cohérents, accessibles.
// ============================================================================

import { useState, useEffect, useRef, createContext, useContext, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { NAV_COLORS, NAV_ICONES, ALIAS_MODULES, encreSurBlanc, fondBanniere, navTexte } from "./navConstants";
import EnTetePage from "./EnTetePage";
import { Icone, IconeTexte } from "./icones";
import { pourcentFr } from "./nombresFr";
import { toast } from "./toasts";
import { MOUVEMENT, animer, annulerAnimations, peutAnimer, mouvementReduit, saisieClavierRecente } from "./mouvement";
import { alpha } from "./alpha";
import { C } from "./couleurs";
import { taillePx } from "./echelleTexte";
import { peutAccederRH } from "./droitsRH";


// ═══════════════════════════════════════════════════════════════════════════════
// TOAST — notifications non bloquantes qui remplacent alert()
// Implémentation dans src/toasts.js (lot L5 : pile, sorties animées, pause,
// glisser pour fermer). La façade reste exportée d'ici, à l'identique :
//   const toast = useToast();  toast.success("Enregistré");
//   import { toast } from "./uxComponents";  toast.error("Échec : ...");
// ═══════════════════════════════════════════════════════════════════════════════
export { ToastProvider, toast, useToast } from "./toasts";

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIRM MODAL — remplace window.confirm() par modal React accessible
// Usage :
//   const confirm = useConfirm();
//   const ok = await confirm({
//     title: "Supprimer le projet ?",
//     message: "Cette action est irréversible.",
//     confirmLabel: "Supprimer",
//     danger: true,
//   });
//   if (ok) { ... }
// ═══════════════════════════════════════════════════════════════════════════════

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const resolverRef = useRef(null);
  // La question AFFICHÉE (miroir synchrone de `state`) : une nouvelle question
  // qui la remplace la résout d'abord comme un refus. Sans cela, la promesse
  // remplacée ne se résolvait jamais et le geste qui l'attendait restait
  // bloqué (relecture du repas, 28/09/2026).
  const stateRef = useRef(null);
  // Le focus initial (bouton d'annulation) SANS défilement : une carte plus
  // haute que l'écran (une confirmation qui liste les anomalies du contrôle de
  // la doctrine) s'ouvrait défilée jusqu'à ses boutons, titre et portée du
  // geste hors de vue (relecture du 12/10/2026). Elle s'ouvre par son titre ;
  // le clavier garde le même bouton.
  const carteRef = useRef(null);
  const annulerRef = useRef(null);
  // Le titre nomme le dialogue pour un lecteur d'écran (role="alertdialog",
  // recette du 03/11/2026 : « Délier la séance » n'était annoncé comme rien).
  const idTitre = `enio-confirm-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  useEffect(() => {
    if (!state || state.input) return;
    const b = annulerRef.current;
    if (b && typeof b.focus === "function") {
      try { b.focus({ preventScroll: true }); } catch (e) { b.focus(); }
    }
    if (carteRef.current) carteRef.current.scrollTop = 0;
  }, [state]);

  const confirm = useCallback((opts) => {
    return new Promise(resolve => {
      // `opts.signal` (AbortSignal, facultatif) : celui qui pose la question
      // peut la RETIRER, et elle se résout alors comme un refus. ModalShell s'en
      // sert quand sa fenêtre disparaît (retour arrière, changement de vue) :
      // sans cela, le dialogue de fermeture restait seul par-dessus la page
      // suivante et bloquait tous les clics (relecture Hestia, 04/10/2026).
      const refus = (st) => (st?.input ? null : st?.extraLabel ? "cancel" : false);
      const signal = opts.signal && typeof opts.signal.addEventListener === "function" ? opts.signal : null;
      if (signal && signal.aborted) { resolve(refus(opts)); return; }
      const precedente = resolverRef.current;
      if (precedente) {
        const st = stateRef.current;
        resolverRef.current = null;
        precedente(refus(st));
      }
      resolverRef.current = resolve;
      if (signal) {
        signal.addEventListener("abort", () => {
          if (resolverRef.current !== resolve) return; // déjà répondue ou remplacée
          const st = stateRef.current;
          resolverRef.current = null;
          stateRef.current = null;
          setState(null);
          resolve(refus(st));
        }, { once: true });
      }
      setInputValue(opts.input?.defaultValue || "");
      setState(stateRef.current = {
        title: opts.title || "Confirmer",
        message: opts.message || "",
        confirmLabel: opts.confirmLabel || "Confirmer",
        cancelLabel: opts.cancelLabel || "Annuler",
        // Bouton optionnel (3 choix). Si présent, la promesse résout une CHAÎNE
        // ("confirm" | "extra" | "cancel") ; sinon un booléen (rétro-compatible).
        extraLabel: opts.extraLabel || null,
        danger: !!opts.danger,
        details: opts.details || null,
        // Champ de saisie optionnel — remplace window.prompt (lot D3 audit RH).
        // { label?, placeholder?, defaultValue?, multiline?, required?, type?, attendu? }
        // `attendu` (21/10/2026) : la confirmation FORTE d'un geste
        // irréversible. Le bouton reste grisé tant que la saisie ne reproduit
        // pas ce texte (casse, accents et espaces indifférents). Le serveur
        // revérifie : ce n'est qu'un garde-fou de saisie.
        // Si présent, la promesse résout la CHAÎNE saisie à la confirmation,
        // ou null à l'annulation.
        input: opts.input || null,
      });
    });
  }, []);

  const close = (result) => {
    const st = state;
    stateRef.current = null;
    setState(null);
    if (resolverRef.current) {
      if (st?.input) {
        const confirmed = result === true || result === "confirm";
        resolverRef.current(confirmed ? inputValue : null);
      } else {
        resolverRef.current(result);
      }
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="enio-voile" style={{
          position:"fixed",top:"var(--app-header-h,56px)",left:0,right:0,bottom:0, background:"rgba(0,0,0,.55)",
          // 20000 : le dialogue de charte doit passer AU-DESSUS de toutes les
          // modales de travail (certaines montent à 10003/10060) — il est leur
          // garde de fermeture. Reste sous les verrous plein écran (99990+),
          // qui ne coexistent jamais avec une modale de travail.
          zIndex:20000, display:"flex", alignItems:"center", justifyContent:"center",
          padding:16,
          animation:"confirmFadeIn .15s ease",
        }} onClick={() => {
          // Règle produit 24/08 : une confirmation qui porte une SAISIE est une
          // fenêtre de travail — le clic à côté ne doit pas la fermer (il
          // vaudrait « Annuler » et jetterait le texte tapé). Sans saisie, le
          // clic extérieur = « Annuler », un choix explicite déjà offert.
          if (state.input) return;
          close(state.extraLabel ? "cancel" : false);
        }}>
          <div ref={carteRef} role="alertdialog" aria-modal="true" aria-labelledby={idTitre} className="enio-glass enio-verre" style={{
            background:"var(--bg-card, #fff)",
            // 18 px = valeur effective de l'ancien jeton --r-md, retiré en L2 (L5 : échelle --radius-*).
            borderRadius:"var(--radius-lg)", padding:"clamp(16px, var(--vw100) * 0.04, 22px) clamp(16px, var(--vw100) * 0.04, 24px)",
            maxWidth:440, width:"100%",
            // Ce dialogue porte les decisions irreversibles (valider un vote,
            // clore un scrutin, supprimer). Il n'avait ni hauteur max ni
            // defilement : un message long poussait ses boutons hors de
            // l'ecran sur iPhone, sans rattrapage possible.
            maxHeight:"min(var(--vh100) * 0.88, 100%)", overflowY:"auto",
            border:"1px solid var(--border-color)",
            animation:"enioRise .28s cubic-bezier(.2,.7,.3,1)",
          }} onClick={e => e.stopPropagation()}>
            <div id={idTitre} style={{fontSize:"var(--fs-corps)", fontWeight:700, color:"var(--text-main)",
              marginBottom:10, fontFamily:"Montserrat,sans-serif"}}>
              {state.danger && <span style={{marginRight:8}}><IconeTexte nom="alert-triangle" couleur="var(--tx-orange)" titre="Attention"/></span>}
              {state.title}
            </div>
            {/* `pre-line` : un message de confirmation qui explique une conséquence
                a besoin de paragraphes. Sans lui, les retours à la ligne étaient
                écrasés et tout arrivait en un seul bloc illisible — donc non lu. */}
            {state.message && (
              <div style={{fontSize:"var(--fs-meta)", color:"var(--text-sub)", marginBottom:16, lineHeight:1.5, whiteSpace:"pre-line"}}>
                {state.message}
              </div>
            )}
            {state.input && (
              <div style={{marginBottom:16}}>
                {state.input.label && (
                  <div style={{fontSize:"var(--fs-micro)", fontWeight:700, color:"var(--text-main)", marginBottom:6}}>
                    {state.input.label}
                  </div>
                )}
                {state.input.multiline ? (
                  <textarea autoFocus value={inputValue} rows={3}
                    onChange={e => setInputValue(e.target.value)}
                    placeholder={state.input.placeholder || ""}
                    style={{width:"100%", padding:"9px 11px", borderRadius:"var(--radius-sm)",
                      border:"1px solid var(--border-color)", background:"var(--bg-card)",
                      color:"var(--text-main)", fontSize:"max(var(--fs-meta), var(--fs-champ-min))", fontFamily:"inherit",
                      boxSizing:"border-box", resize:"vertical"}}/>
                ) : (
                  <input autoFocus value={inputValue}
                    type={state.input.type || "text"}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !saisieRefusee(state.input, inputValue)) {
                        close(state.extraLabel ? "confirm" : true);
                      }
                    }}
                    placeholder={state.input.placeholder || ""}
                    style={{width:"100%", padding:"9px 11px", borderRadius:"var(--radius-sm)",
                      border:"1px solid var(--border-color)", background:"var(--bg-card)",
                      color:"var(--text-main)", fontSize:"max(var(--fs-meta), var(--fs-champ-min))", fontFamily:"inherit",
                      boxSizing:"border-box"}}/>
                )}
              </div>
            )}
            {state.details && (
              <div style={{fontSize:"var(--fs-micro)", color:"var(--text-sub)", marginBottom:16,
                padding:"8px 10px", background:"var(--card-inner)", borderRadius:"var(--radius-sm)",
                fontFamily:"monospace", whiteSpace:"pre-wrap", maxHeight:120, overflow:"auto"}}>
                {state.details}
              </div>
            )}
            {/* Plancher de 44 pt AU DOIGT (var(--cible-min) vaut 0px au
                clavier-souris) : "Annuler" et "Valider definitivement"
                faisaient 30 px de haut — au pouce, on validait ce qu'on
                voulait annuler. Le rembourrage et l'ecart restent ceux du
                bureau : c'est la hauteur minimale qui fait la cible. */}
            <div style={{display:"flex", gap:8, justifyContent:"flex-end", flexWrap:"wrap"}}>
              <button ref={annulerRef} className="btn btn-ghost"
                onClick={() => close(state.extraLabel ? "cancel" : false)}
                style={{minHeight:"var(--cible-min)"}}>
                {state.cancelLabel}
              </button>
              {state.extraLabel && (
                <button className="btn btn-navy" onClick={() => close("extra")}
                  style={{minHeight:"var(--cible-min)"}}>
                  {state.extraLabel}
                </button>
              )}
              <button className={"btn " + (state.danger ? "btn-red" : state.extraLabel ? "btn-ghost" : "btn-navy")}
                onClick={() => close(state.extraLabel ? "confirm" : true)}
                disabled={saisieRefusee(state.input, inputValue)}
                style={{minHeight:"var(--cible-min)"}}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
          <style>{`@keyframes confirmFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// La saisie d'une confirmation est-elle insuffisante ? (requise et vide, ou
// différente du texte attendu d'une confirmation forte)
const normConfirm = (v) => String(v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
function saisieRefusee(input, valeur) {
  if (!input) return false;
  if (input.required && !String(valeur || "").trim()) return true;
  if (input.attendu != null && normConfirm(valeur) !== normConfirm(input.attendu)) return true;
  return false;
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback : window.confirm (garde la même interface async)
    return async (opts) => window.confirm(opts.message || opts.title || "Confirmer ?");
  }
  return ctx;
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS BADGE — badge coloré uniforme pour statuts (consultations,
// implantations, demandes, etc.)
// Usage :
//   <StatusBadge status="ouverte" type="consultation" />
// ═══════════════════════════════════════════════════════════════════════════════

const STATUS_MAP = {
  // Consultations
  ouverte:        { label: "Ouverte",          color: "var(--tx-green)",  icon: "🟢" },
  close:          { label: "Clôturée",         color: C.gray,   icon: "🔒" },
  en_cours:       { label: "En cours",         color: "var(--tx-orange)", icon: "⏳" },
  brouillon:      { label: "Brouillon",        color: C.gray,   icon: "📝" },
  // Saisie Dataviz (statut d'une entrée)
  soumis:               { label: "Soumis",              color: "var(--tx-orange)", icon: "📤" },
  valide:               { label: "Validé",              color: "var(--tx-green)",  icon: "✅" },
  rejete:               { label: "Rejeté",              color: "var(--tx-red)",    icon: "❌" },
  correction_demandee:  { label: "Correction demandée", color: "var(--tx-violet)", icon: "🔄" },
  // Propositions d'items (consultation notation_items)
  refuse:               { label: "Refusé",              color: "var(--tx-red)",    icon: "✕" },
  // Demandes
  en_attente:     { label: "En attente",       color: "var(--tx-gold)",   icon: "⏳" },
  pending:        { label: "En attente",       color: "var(--tx-gold)",   icon: "⏳" },
  validee:        { label: "Validée",          color: "var(--tx-green)",  icon: "✅" },
  refusee:        { label: "Refusée",          color: "var(--tx-red)",    icon: "❌" },
  // Projets — phases (refonte 2026-04 : nouvelles valeurs `phase`)
  idee:                { label: "Idée",                     color: C.gray,    icon: "💡" },
  cadrage:             { label: "Cadrage",                  color: "var(--tx-gold)",    icon: "🔍" },
  instruction:         { label: "Instruction",              color: "var(--tx-blue)",    icon: "🚧" },
  // `deploiement` : voir aussi engagement mutuelles plus bas (alias label)
  // Solutions — disponibilité (refonte 2026-04)
  // `ouverte` est déjà mappé plus haut pour les consultations (label "Ouverte"
  // identique). On ne le redéfinit pas. Pour le contexte "Solution disponible",
  // on garde `ouverte` qui matche déjà le label "Ouverte".
  reservee:            { label: "Réservée",                 color: "var(--tx-gold)",    icon: "🟡" },
  fermee:              { label: "Fermée",                   color: C.gray,    icon: "⚪" },
  // Missions — cadence de revue (refonte 2026-04)
  trimestrielle:       { label: "Revue trimestrielle",      color: "var(--tx-violet)",  icon: "📅" },
  semestrielle:        { label: "Revue semestrielle",       color: "var(--tx-violet)",  icon: "📅" },
  annuelle:            { label: "Revue annuelle",           color: "var(--tx-violet)",  icon: "📅" },
  continue:            { label: "Continue",                 color: "var(--tx-violet)",  icon: "♾" },
  // ─── Statut de maturité (axe sémantique principal — refonte 2026) ────────
  // Cf. App.jsx STATUTS_MATURITE / getStatutMaturite()
  activable:           { label: "Activable",                color: "var(--tx-green)",   icon: "🟢" },
  construction:        { label: "En construction",          color: "var(--tx-gold)",    icon: "🟠" },
  etude:               { label: "À l'étude",                color: "var(--tx-blue)",    icon: "🔵" },
  acquis:              { label: "Acquis",                   color: "var(--tx-violet)",  icon: "🟣" },
  mission_permanente:  { label: "Mission permanente",       color: "var(--tx-teal)",    icon: "🔄" },
  // Projets — phases legacy (avant migration)
  exploratoire:        { label: "Phase exploratoire",       color: "var(--tx-gold)",    icon: "🔍" },
  intermediaire:       { label: "Phase intermédiaire",      color: "var(--tx-blue)",    icon: "🚧" },
  conclusion:          { label: "Phase de conclusion",      color: "var(--tx-green)",   icon: "🏁" },
  partenariat_ouvert:  { label: "Partenariat ouvert",       color: "var(--tx-green)",   icon: "🤝" },
  solution_ouverte:    { label: "Solution ouverte",         color: "var(--tx-violet)",  icon: "🔓" },
  attente:             { label: "En attente de lancement",  color: C.gray,    icon: "⏸" },
  realise:             { label: "Réalisé / Clôturé",        color: "var(--tx-green)",   icon: "✔" },
  travail_continu:     { label: "Travail continu",          color: "var(--tx-orange)",  icon: "🔁" },
  abandonne:           { label: "Abandonné",                color: "var(--tx-red)",     icon: "✕" },
  // Projets — legacy
  actif:          { label: "Actif",            color: "var(--tx-teal)",   icon: "▶" },
  clos:           { label: "Clos",             color: C.gray,   icon: "■" },
  archive:        { label: "Archivé",          color: C.gray,   icon: "📦" },
  // Engagements mutuelle
  pas_concernee:        { label: "Pas concernée",         color: C.gray,    icon: "⊝" },
  non_interesse:        { label: "Pas intéressée",        color: "var(--tx-red)",     icon: "⊗" },
  etudie:               { label: "Étudie l'opportunité",  color: "var(--tx-gold)",    icon: "🔍" },
  attente_enio:         { label: "Enio en attente",       color: "var(--tx-violet)",  icon: "⏳" },
  participe:            { label: "Participe",             color: "var(--tx-blue)",    icon: "🙋" },
  deploiement:          { label: "En déploiement",        color: "var(--tx-blue)",    icon: "🚀" },
  engage:               { label: "A déployé",             color: "var(--tx-green)",   icon: "✅" },
  propose_distribution: { label: "Propose en distrib.",   color: "var(--tx-teal)",    icon: "📤" },
  distribue_offre:      { label: "Distribue l'offre",     color: "var(--tx-green)",   icon: "📬" },
  non_interessee:       { label: "Non intéressée",        color: C.gray,    icon: "⊗" },
  // Inscriptions / comptes
  actif_user:     { label: "Actif",            color: "var(--tx-green)",  icon: "✅" },
  invite:         { label: "Invité",           color: "var(--tx-gold)",   icon: "✉️" },
  non_invite:     { label: "Non invité",       color: "var(--tx-red)",    icon: "✕" },
  inactif:        { label: "Inactif",          color: C.gray,   icon: "⏸" },
};

export function StatusBadge({ status, type = "default", size = "sm", label, icon, color, hideIcon = false }) {
  if (!status && !label) return null;
  const cfg = (status && STATUS_MAP[status]) || { label: label || String(status || "—"), color: C.gray, icon: "•" };
  const finalLabel = label ?? cfg.label;
  const finalIcon = icon ?? cfg.icon;
  const finalColor = color ?? cfg.color;
  const padding = size === "lg" ? "5px 12px" : size === "xs" ? "1px 6px" : "2px 8px";
  const fontSize = size === "lg" ? 12 : size === "xs" ? 9 : 10;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: padding,
      // Fond + contour : garantit un badge lisible sur fond clair (mode jour)
      // ET sur fond sombre (mode nuit). Sans le border, le fond à 13% opacité
      // devient quasi-invisible sur card sombre.
      // color-mix : finalColor peut être un var(--tx-*) (STATUS_MAP) → un suffixe
      // alpha hex (`+22`) donnerait du CSS invalide (badge fantôme sans fond/bord).
      background: `color-mix(in srgb, ${finalColor} 13%, transparent)`,
      color: navTexte(finalColor),
      border: `1px solid color-mix(in srgb, ${finalColor} 33%, transparent)`,
      borderRadius: "var(--radius-md)",
      fontSize: taillePx(fontSize),
      fontWeight: 700,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
    }}>
      {!hideIcon && finalIcon && <span style={{fontSize: taillePx(fontSize + 1)}}>{finalIcon}</span>}
      <span>{finalLabel}</span>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GLOSSARY TOOLTIP — explique le jargon Enio au survol
// Usage :
//   <GlossaryTerm term="CA">Conseil d'administration</GlossaryTerm>
//   ou : <GlossaryTerm term="BOAMP"/> (utilise le dictionnaire prédéfini)
// ═══════════════════════════════════════════════════════════════════════════════

const GLOSSARY = {
  CA: "Conseil d'administration — instance de gouvernance qui valide les orientations et décisions importantes.",
  AG: "Assemblée Générale — réunion de tous les délégués représentant les adhérents, pour approbation des comptes et orientations.",
  CODIR: "Comité de direction — instance opérationnelle qui pilote l'exécution au quotidien.",
  CD: "CODIR / Comité de direction.",
  DG: "Directeur Général — dirigeant opérationnel d'une mutuelle.",
  PR: "Président — dirigeant élu représentant la gouvernance politique.",
  Bureau: "Bureau — instance restreinte entre CA et CODIR, regroupant les vice-présidents.",
  BOAMP: "Bulletin Officiel des Annonces de Marchés Publics — publie les appels d'offres publics auxquels les mutuelles peuvent répondre.",
  ACPR: "Autorité de Contrôle Prudentiel et de Résolution — régulateur français des assurances et mutuelles.",
  DORA: "Digital Operational Resilience Act — règlement européen sur la résilience numérique du secteur financier.",
  FNMF: "Fédération Nationale de la Mutualité Française — principale organisation représentative des mutuelles.",
  UGM: "Union de Groupe Mutualiste — structure juridique d'association de mutuelles (comme Enio).",
  S: "Sinistres — prestations versées aux adhérents.",
  P: "Primes — cotisations perçues des adhérents.",
  CPV: "Common Procurement Vocabulary — nomenclature européenne qui classe les marchés publics par type de service.",
  TOTP: "Time-based One-Time Password — code à 6 chiffres qui change toutes les 30 secondes, généré par une app d'authentification.",
  "2FA": "Authentification à deux facteurs — sécurité supplémentaire en plus du mot de passe (via app d'authentification).",
  AMO: "Assurance Maladie Obligatoire — le régime de base de la sécurité sociale.",
  // Sprint 2 D2-7 — Termes LCB-FT / Gel des avoirs & PPE
  "LCB-FT": "Lutte Contre le Blanchiment et le Financement du Terrorisme — obligation légale des mutuelles : vérifier que leurs adhérents/bénéficiaires ne sont pas sur des listes de sanctions ou exposés politiquement.",
  PPE: "Personne Politiquement Exposée — personne qui exerce ou a exercé des fonctions politiques importantes (élu, ministre, dirigeant d'OPCA…). Imposent une vigilance renforcée selon la directive UE 2015/849.",
  Tracfin: "Service de renseignement financier français — destinataire des déclarations de soupçon LCB-FT. Toute mutuelle doit déclarer les transactions suspectes via téléprocédure Ermes.",
  Ermes: "Téléprocédure officielle Tracfin pour télédéclarer les soupçons de blanchiment. Aramis génère le XML conforme + le PDF justificatif.",
  SDN: "Specially Designated Nationals — liste OFAC (US Treasury) des personnes physiques/morales sanctionnées par les États-Unis (terrorisme, prolifération, sanctions économiques).",
  OFAC: "Office of Foreign Assets Control — bureau du US Treasury qui publie la liste SDN. Largement utilisée à l'international comme référence des sanctions.",
  HATVP: "Haute Autorité pour la Transparence de la Vie Publique — publie le répertoire des représentants d'intérêts (lobbies) en France, avec dirigeants et collaborateurs.",
  RNE: "Répertoire National des Élus — base ouverte data.gouv.fr listant tous les élus locaux et nationaux français. Source PPE principale pour Aramis.",
  MRZ: "Machine-Readable Zone — zone OCR au bas des passeports/CNI utilisée pour automatiser la lecture des données d'identité (utilisé en KYC avancé).",
  HIT: "Correspondance forte — score de match ≥ 0.85 contre une entrée de liste de sanctions. Nécessite une action LCB-FT (vigilance renforcée, déclaration Tracfin…).",
  DOUTE: "Correspondance partielle — score entre 0.65 et 0.85. À examiner manuellement (homonymie, date de naissance différente…). Peut être clos en faux positif.",
  CLEAR: "Aucune correspondance — score < 0.65. Criblage favorable, archivage automatique.",
  SIREN: "Système d'Identification du Répertoire des Entreprises — identifiant unique à 9 chiffres d'une personne morale en France.",
};

export function GlossaryTerm({ term, children }) {
  const definition = GLOSSARY[term] || children || term;
  return (
    <span
      style={{
        // Un soulignement pointillé (le terme du glossaire), pas un filet.
        textDecoration: "underline dotted var(--text-sub)",
        textUnderlineOffset: "3px",
        cursor: "help",
        textDecorationSkipInk: "none",
      }}
      title={typeof definition === "string" ? definition : term}>
      {children || term}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// useUnsavedChanges — prévient l'utilisateur avant qu'il ne perde sa saisie
// Usage dans un formulaire ou modal :
//   const { dirty, markDirty, markClean, confirmLeave } = useUnsavedChanges();
//   <input onChange={e => { setX(e.target.value); markDirty(); }}/>
//   <button onClick={async () => { if (await confirmLeave()) onClose(); }}>Annuler</button>
//   onSave = async () => { await save(); markClean(); onClose(); };
// • Active automatiquement `beforeunload` quand dirty=true (ferme onglet / refresh).
// • `confirmLeave()` retourne true si propre ou si l'utilisateur confirme la perte.
// ═══════════════════════════════════════════════════════════════════════════════

export function useUnsavedChanges(options = {}) {
  const [dirty, setDirty] = useState(false);
  const confirm = useConfirm();
  const message = options.message || "Vous avez des modifications non enregistrées. Quitter sans sauvegarder ?";

  // Bloque la fermeture d'onglet / refresh quand dirty
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = message; // Chrome requiert returnValue
      return message;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, message]);

  const markDirty = useCallback(() => setDirty(true), []);
  const markClean = useCallback(() => setDirty(false), []);

  // Vérifie si on peut quitter — si dirty, demande confirmation via modal
  const confirmLeave = useCallback(async () => {
    if (!dirty) return true;
    const ok = await confirm({
      title: "Modifications non enregistrées",
      message,
      confirmLabel: "Quitter sans sauvegarder",
      cancelLabel: "Continuer la saisie",
      danger: true,
    });
    if (ok) setDirty(false);
    return ok;
  }, [dirty, confirm, message]);

  // Variante 3 choix : « Sauvegarder et quitter » / « Quitter sans sauvegarder »
  // / « Continuer la saisie ». Retourne "save" | "discard" | "cancel".
  const confirmLeaveOrSave = useCallback(async () => {
    if (!dirty) return "discard";
    const r = await confirm({
      title: "Modifications non enregistrées",
      message,
      extraLabel: "Sauvegarder et quitter",
      confirmLabel: "Quitter sans sauvegarder",
      cancelLabel: "Continuer la saisie",
      danger: true,
    });
    if (r === "extra") { setDirty(false); return "save"; }
    if (r === "confirm") { setDirty(false); return "discard"; }
    return "cancel";
  }, [dirty, confirm, message]);

  return { dirty, markDirty, markClean, confirmLeave, confirmLeaveOrSave };
}
// NOTE (24/08/2026) : pour les MODALES, préférer <ModalShell> ci-dessous, qui
// applique la règle produit « jamais de fermeture silencieuse » (le
// confirmLeaveOrSave ci-dessus retourne "discard" sans dialogue quand !dirty —
// comportement conservé pour ses appelants historiques, mais non conforme à la
// règle pour une fermeture de fenêtre).

// ═══════════════════════════════════════════════════════════════════════════════
// RÈGLE PRODUIT (Pierre, 24/08/2026) — « Le clic à côté ne ferme jamais »
// Toute fenêtre de TRAVAIL (formulaire, éditeur, aperçu, assistant) ne se ferme
// JAMAIS silencieusement au clic hors de la fenêtre ni sur Échap : un dialogue
// de choix explicite s'ouvre toujours.
//   • modifications en cours + sauvegarde possible :
//       « Enregistrer et fermer » / « Fermer sans enregistrer » / « Annuler »
//   • modifications en cours sans sauvegarde possible :
//       « Fermer sans enregistrer » / « Annuler »
//   • aucune modification : « Fermer » / « Annuler »
// Implémentation : <ModalShell> (le calque + Échap + dialogue) + useDirtyFrom
// (détection des modifications par instantané). Le test src/modalOverlayLock
// .test.js fait échouer la CI si un calque fermant réapparaît hors liste blanche.
// ═══════════════════════════════════════════════════════════════════════════════

// Dialogue de fermeture adaptatif. `confirm` = la fonction retournée par
// useConfirm() (passée en argument : ceci n'est pas un hook).
// Retourne "save" | "discard" | "cancel" — jamais de fermeture silencieuse.
// `signal` (facultatif) retire la question : réponse « cancel ».
export async function confirmClose(confirm, { dirty, canSave, closeMessage, signal } = {}) {
  if (dirty && canSave) {
    const r = await confirm({
      title: "Fermer cette fenêtre ?",
      message: closeMessage || "Des modifications ne sont pas enregistrées.",
      extraLabel: "Enregistrer et fermer",
      confirmLabel: "Fermer sans enregistrer",
      cancelLabel: "Annuler",
      danger: true,
      signal,
    });
    return r === "extra" ? "save" : r === "confirm" ? "discard" : "cancel";
  }
  if (dirty) {
    const ok = await confirm({
      title: "Fermer cette fenêtre ?",
      message: closeMessage || "Des modifications ne sont pas enregistrées.",
      confirmLabel: "Fermer sans enregistrer",
      cancelLabel: "Annuler",
      danger: true,
      signal,
    });
    return ok ? "discard" : "cancel";
  }
  const ok = await confirm({
    title: "Fermer cette fenêtre ?",
    message: closeMessage || "Aucune modification en cours.",
    confirmLabel: "Fermer",
    cancelLabel: "Annuler",
    signal,
  });
  return ok ? "discard" : "cancel";
}

// Sérialisation stable pour l'instantané de useDirtyFrom : Set → tableau trié,
// Map → entrées triées, undefined → null, fonctions/File → ignorés (les uploads
// utilisent le repli « dirty au premier changement »).
function stableStringify(value) {
  const seen = new Set();
  const walk = (v) => {
    if (v === undefined) return null;
    if (v === null || typeof v === "number" || typeof v === "boolean" || typeof v === "string") return v;
    if (typeof v === "function") return null;
    if (typeof File !== "undefined" && v instanceof File) return `[File:${v.name}:${v.size}]`;
    if (v instanceof Date) return v.toISOString();
    if (v instanceof Set) return Array.from(v).map(walk).sort();
    if (v instanceof Map) return Array.from(v.entries()).map(([k, x]) => [k, walk(x)]).sort();
    if (Array.isArray(v)) return v.map(walk);
    if (typeof v === "object") {
      if (seen.has(v)) return "[cycle]";
      seen.add(v);
      const out = {};
      for (const k of Object.keys(v).sort()) out[k] = walk(v[k]);
      seen.delete(v);
      return out;
    }
    return String(v);
  };
  try { return JSON.stringify(walk(value)); } catch { return String(Math.random()); }
}

// Détection de modifications par instantané : capture la valeur au premier rendu
// « prêt » et compare à chaque rendu. Pour les modales multi-useState, passer un
// objet agrégé : useDirtyFrom({ form, docs, listes:[...sel] }).
// `ready:false` pour les formulaires initialisés après un fetch.
export function useDirtyFrom(value, { ready = true } = {}) {
  const initialRef = useRef(null);
  const snap = ready ? stableStringify(value) : null;
  if (ready && initialRef.current === null) initialRef.current = snap;
  const dirty = ready && initialRef.current !== null && snap !== initialRef.current;
  const reset = useCallback(() => { initialRef.current = null; }, []);
  // `fixer(valeur)` : la référence devient CETTE valeur (ce qui vient d'être
  // enregistré), pas l'état du rendu suivant ; une saisie faite pendant
  // l'enregistrement reste donc « non enregistrée » (revue du lot 13).
  const fixer = useCallback((v) => { initialRef.current = stableStringify(v); }, []);
  return { dirty, reset, fixer };
}

// Pile des shells montés : seul le shell au SOMMET réagit à Échap (modales
// empilées). Module-locale, pas de contexte nécessaire. Chaque jeton porte
// `fermer(source)` : la demande de fermeture de SON shell, dialogue compris.
const _modalShellStack = [];

/** Nombre de fenêtres de travail (<ModalShell>) ouvertes. */
export const fenetresDeTravailOuvertes = () => _modalShellStack.length;

/**
 * Ferme les fenêtres de travail ouvertes, du sommet vers le fond, chacune PAR
 * SON DIALOGUE DE CHOIX (règle du 24/08 : jamais de fermeture silencieuse).
 * Pour une navigation qui n'émane pas de la fenêtre : lien d'une notification
 * suivi depuis le centre, clic sur une notification système. Résout true
 * quand plus aucune n'est ouverte ; false dès que la personne en garde une
 * (« Annuler », garde métier, échec de l'enregistrement) : l'appelant renonce.
 * @returns {Promise<boolean>}
 */
export async function fermerFenetresDeTravail(source = "navigation") {
  for (let tour = 0; _modalShellStack.length > 0; tour += 1) {
    if (tour >= 20) return false;
    const haut = _modalShellStack[_modalShellStack.length - 1];
    const ferme = haut && typeof haut.fermer === "function" ? await haut.fermer(source) : false;
    if (!ferme) return false;
    // Fermeture acceptée : le parent démonte la fenêtre au rendu suivant.
    for (let i = 0; i < 40 && _modalShellStack.includes(haut); i += 1) {
      await new Promise((r) => setTimeout(r, 25));
    }
    if (_modalShellStack.includes(haut)) return false;   // gardée montée par son parent
  }
  return true;
}

/**
 * Garde de navTo pour un lien de notification (`opts.lienNotification`) ou un
 * geste qui annonce des paramètres à la vue courante (`opts.annonceVueCourante`,
 * l'objet s'ouvrirait sous la fenêtre) : aucune
 * fenêtre de travail ouverte, false (naviguer maintenant). Sinon true : la
 * navigation est DIFFÉRÉE, `reprendre()` n'est appelé qu'une fois chaque
 * fenêtre fermée par son dialogue, et jamais si la personne en garde une.
 * @param {object|null} opts  options de navTo
 * @param {function(): void} reprendre  relance la même navigation
 * @returns {boolean} true si navTo doit s'arrêter là
 */
export function differerSiFenetreOuverte(opts, reprendre) {
  if (!opts || !(opts.lienNotification || opts.annonceVueCourante) || _modalShellStack.length === 0) return false;
  fermerFenetresDeTravail().then((fermees) => { if (fermees) reprendre(); }).catch(() => {});
  return true;
}
// Feuilles ouvertes (téléphone) : tant qu'il y en a une, <html> porte
// data-feuille-ouverte (voir l'effet de ModalShell et CSS_PRIMITIVES).
let _feuillesOuvertes = 0;

// ─────────────────────────────────────────────────────────────────────────────
// <ModalShell> — LE calque de modale de l'application.
// Remplace le patron copié-collé « <div calque onClick={onClose}><div carte
// onClick={stopPropagation}>… ». Clic extérieur et Échap ouvrent TOUJOURS le
// dialogue de choix (confirmClose) ; la fermeture effective passe par
// onRequestClose une fois le choix confirmé.
//
// Props :
//   onRequestClose  (requis)  ferme réellement la modale (setState parent)
//   onSave?         sync/async. Active « Enregistrer et fermer » quand dirty.
//                   Contrat : throw ⇒ toast.error + la modale RESTE ouverte ;
//                   `return false` ⇒ reste ouverte silencieusement (le site a
//                   affiché sa propre validation/erreur).
//   dirty?          bool — modifications en cours (useDirtyFrom ou état local)
//   closable?       bool | () => bool — garde métier (busy/phase) : si falsy,
//                   clic extérieur, Échap et ✕ intégré sont inertes.
//   title?          en-tête standard (titre + ✕) rendu par le shell
//   closeRef?       ref — le shell y écrit requestClose (✕/« Fermer » custom).
//                   requestClose(source?) rend une promesse : true si la
//                   fenêtre s'est fermée, false sinon (lot L5, additif).
//   closeMessage?   message dédié du dialogue de fermeture (ex. clé API)
//   zIndex?=5000  wide?  maxWidth?  align?="center"|"top"
//   overlayStyle? cardStyle?   merges pour les spécificités du site
//   initialFocusRef?            focus initial (sinon la carte, sans écraser un
//                               autofocus enfant)
//   presentation?="auto"        lot L5 : "auto" | "centre" | "panneau" (voir
//                               resoudrePresentation ci-dessous)
//   labelledBy?                 id du titre que la fenêtre dessine elle-même
//                               (aria-labelledby) : panneau des réglages du
//                               bandeau de commande (27/10/2026)
//
// MOUVEMENT (lot L5, 28/09/2026 ; synthèse §5.2 régime spatial, §11) :
//   • entrée jouée au MONTAGE de la carte (rappel de ref, avant la première
//     peinture, sans effet côté serveur) : voile en fondu 200 ms ; carte
//     opacité + échelle(--motion-scale-in) 260 ms --ease-out (centre),
//     translateX(24px) + opacité 260 ms (panneau, tiroir), translateY(100 %)
//     → 0 en 420 ms --ease-drawer (feuille) ; fondu 160 ms sous mouvement
//     réduit ; RIEN quand la fenêtre s'ouvre au clavier ;
//   • sortie jouée DANS requestClose, APRÈS le choix du dialogue et juste
//     avant onRequestClose : 160 ms (centre, panneau), 240 ms (feuille) ; rien
//     au clavier (Échap) ; si le parent garde la fenêtre montée malgré
//     onRequestClose, l'animation est annulée et la fenêtre réapparaît ;
//   • sous jsdom (tests) el.animate n'existe pas : tout est court-circuité et
//     la fermeture reste synchrone, comme avant L5.
// La GARDE est intacte : Échap, voile, ✕, closeRef ET le geste de la feuille
// passent tous par requestClose, donc par le dialogue de choix.
// ─────────────────────────────────────────────────────────────────────────────

// Présentation d'une fenêtre (lot L5) — décidée UNE fois, au montage :
//   « feuille » au téléphone (html[data-coquille="tel"], posé par
//               installerClasseCoquille depuis l'écran physique), sauf
//               presentation="centre" ;
//   « panneau » panneau latéral droit au bureau et sur tablette, sur demande
//               (presentation="panneau") ;
//   « tiroir »  tiroir latéral DÉJÀ dessiné par l'appelant (overlayStyle
//               justifyContent:"flex-end" + alignItems:"stretch") : sa
//               géométrie est gardée, seule l'entrée devient latérale ;
//   « centre »  la modale d'avant L5, pour tout le reste.
// Interrupteur de repli (critique L5, M12) : false rend au téléphone la carte
// centrée d'avant, sans annuler le reste du lot. Un seul endroit.
export const FEUILLE_AU_TELEPHONE = true;
export function resoudrePresentation({ presentation = "auto", coquille = "bureau", overlayStyle } = {}) {
  if (coquille === "tel") return presentation === "centre" || !FEUILLE_AU_TELEPHONE ? "centre" : "feuille";
  if (presentation === "panneau") return "panneau";
  if (presentation === "centre") return "centre";
  const o = overlayStyle || {};
  return o.justifyContent === "flex-end" && o.alignItems === "stretch" ? "tiroir" : "centre";
}

const lireCoquille = () => {
  if (typeof document === "undefined" || !document.documentElement) return "bureau";
  return document.documentElement.getAttribute("data-coquille") || "bureau";
};

// Ce que la feuille et le panneau RÉÉCRIVENT dans le cardStyle d'un appelant
// (sa géométrie de modale centrée n'a pas de sens pour eux). Le reste (fond,
// bordure, couleur, disposition, rembourrage) est gardé. En « centre » et en
// « tiroir », rien n'est réécrit.
const GEOMETRIE_CARTE = ["borderRadius", "maxWidth", "width", "minWidth", "margin", "marginTop",
  "marginBottom", "marginLeft", "marginRight", "maxHeight", "height", "minHeight", "boxShadow"];
// Ce que la feuille passe à son CORPS, qui défile (le cadre, lui, ne défile
// pas : il porte la poignée et l'en-tête).
const DISPOSITION_CORPS = ["padding", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "display", "flexDirection", "flexWrap", "gap", "rowGap", "columnGap", "alignItems",
  "justifyContent", "overflow", "overflowX", "overflowY", "textAlign", "color", "fontFamily",
  "lineHeight"];
// Ce que la feuille réécrit dans l'overlayStyle d'un appelant.
const DISPOSITION_VOILE = ["padding", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft",
  "display", "flexDirection", "alignItems", "justifyContent", "overflow", "overflowX", "overflowY",
  "WebkitOverflowScrolling"];
const sansCles = (obj, cles) => {
  const o = { ...(obj || {}) };
  cles.forEach((k) => { delete o[k]; });
  return o;
};
const seulementCles = (obj, cles) => {
  const o = {};
  cles.forEach((k) => { if (obj && obj[k] !== undefined) o[k] = obj[k]; });
  return o;
};

// Entrée d'une fenêtre, jouée depuis le rappel de ref de la carte (le voile,
// son parent, est déjà dans le document). Rien sans WAAPI ni au clavier.
function jouerEntreeFenetre(carte, mode) {
  if (!peutAnimer(carte) || saisieClavierRecente()) return;
  const voile = carte.parentElement;
  const sortie = MOUVEMENT.courbes.sortie;
  let fond = null;
  try { fond = voile ? getComputedStyle(voile).backgroundColor : null; } catch { fond = null; }
  const fondu = (duree) => {
    if (voile && fond) animer(voile, [{ backgroundColor: "rgba(0,0,0,0)" }, { backgroundColor: fond }], { duration: duree, easing: sortie });
  };
  if (mouvementReduit()) {
    animer(carte, [{ opacity: 0 }, { opacity: 1 }], { duration: MOUVEMENT.durees.rapide, easing: sortie });
    fondu(MOUVEMENT.durees.rapide);
    return;
  }
  if (mode === "feuille") {
    animer(carte, [{ transform: "translateY(100%)" }, { transform: "translateY(0px)" }],
      { duration: MOUVEMENT.durees.feuille, easing: MOUVEMENT.courbes.tiroir });
  } else if (mode === "panneau" || mode === "tiroir") {
    animer(carte, [{ opacity: 0, transform: "translateX(24px)" }, { opacity: 1, transform: "none" }],
      { duration: MOUVEMENT.durees.modale, easing: sortie });
  } else {
    animer(carte, [{ opacity: 0, transform: `scale(${MOUVEMENT.geometrie.echelleEntree})` }, { opacity: 1, transform: "none" }],
      { duration: MOUVEMENT.durees.modale, easing: sortie });
  }
  fondu(MOUVEMENT.durees.vue);
}

// Sortie d'une fenêtre : rend une promesse à attendre, ou null quand il n'y a
// rien à jouer (jsdom, Échap, dernière saisie au clavier) — la fermeture est
// alors immédiate, exactement comme avant L5.
function jouerSortieFenetre(carte, mode, source) {
  if (!peutAnimer(carte) || source === "clavier" || saisieClavierRecente()) return null;
  const voile = carte.parentElement;
  const reduit = mouvementReduit();
  const duree = !reduit && mode === "feuille" ? MOUVEMENT.durees.feuilleSortie : MOUVEMENT.durees.rapide;
  const options = { duration: duree, easing: MOUVEMENT.courbes.sortie, fill: "forwards" };
  let depart = "none";
  try { depart = getComputedStyle(carte).transform || "none"; } catch { depart = "none"; }
  annulerAnimations(carte);
  let images;
  if (reduit) images = [{ opacity: 1 }, { opacity: 0 }];
  else if (mode === "feuille") images = [{ transform: depart === "none" ? "translateY(0px)" : depart }, { transform: "translateY(100%)" }];
  else if (mode === "panneau" || mode === "tiroir") images = [{ opacity: 1, transform: "none" }, { opacity: 0, transform: "translateX(24px)" }];
  else images = [{ opacity: 1, transform: "none" }, { opacity: 0, transform: `scale(${MOUVEMENT.geometrie.echelleEntree})` }];
  const a = animer(carte, images, options);
  if (voile) {
    let fond = null;
    try { fond = getComputedStyle(voile).backgroundColor; } catch { fond = null; }
    if (fond) animer(voile, [{ backgroundColor: fond }, { backgroundColor: "rgba(0,0,0,0)" }], options);
  }
  return a ? a.finished.then(() => true, () => true) : null;
}

export function ModalShell({
  onRequestClose,
  onSave,
  dirty = false,
  closable = true,
  title,
  closeRef,
  closeMessage,
  zIndex = 5000,
  wide = false,
  maxWidth,
  align = "center",
  overlayStyle,
  cardStyle,
  initialFocusRef,
  presentation = "auto",
  labelledBy,
  // Rendue dans <body> (createPortal) plutôt qu'à sa place dans la page : pour
  // une fenêtre ouverte depuis une carte dont un ancêtre porte un transform,
  // une animation de transform ou un backdrop-filter, qui deviendrait le bloc
  // contenant du voile fixe (relecture du PV signé, 03/11/2026 : la fenêtre
  // de dépôt rognée dans la carte « Signatures », sous .fi.adm-cadre). À la
  // demande, pas d'office : un portail sort la fenêtre des règles CSS posées
  // par l'ancêtre (`.x .y`) et des propriétés personnalisées qu'il hérite
  // (teinte d'un module…), que 166 fenêtres n'ont pas toutes été relues pour.
  portail = false,
  children,
}) {
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const askingRef = useRef(false);
  // La question de fermeture en cours : retirée si la fenêtre disparaît avant
  // la réponse (retour arrière, changement de vue). Sinon le dialogue survivait
  // à sa fenêtre et « Enregistrer et fermer » aurait enregistré depuis une page
  // déjà quittée (relecture Hestia, 04/10/2026).
  const questionRef = useRef(null);
  const cardRef = useRef(null);
  const poigneeRef = useRef(null);
  const teteRef = useRef(null);
  const monteRef = useRef(true);
  const entreeFaiteRef = useRef(false);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = {};
  // Décidée une fois : le téléphone ne devient pas un bureau en pleine saisie.
  const [mode] = useState(() => resoudrePresentation({ presentation, coquille: lireCoquille(), overlayStyle }));
  const feuille = mode === "feuille";
  const panneau = mode === "panneau";

  const isClosable = typeof closable === "function" ? !!closable() : !!closable;

  // Rappel de ref : pose la carte et joue l'entrée UNE fois, avant la peinture.
  const poserCarte = useCallback((el) => {
    cardRef.current = el;
    if (!el || entreeFaiteRef.current) return;
    entreeFaiteRef.current = true;
    jouerEntreeFenetre(el, mode);
  }, [mode]);

  const requestClose = useCallback(async (source) => {
    if (askingRef.current || saving) return false;
    const okToClose = typeof closable === "function" ? !!closable() : !!closable;
    if (!okToClose) return false;
    askingRef.current = true;
    const question = typeof AbortController === "function" ? new AbortController() : null;
    questionRef.current = question;
    try {
      const action = await confirmClose(confirm, { dirty, canSave: !!onSave, closeMessage, signal: question ? question.signal : undefined });
      if (questionRef.current === question) questionRef.current = null;
      if (action === "cancel" || !monteRef.current) return false;
      if (action === "save") {
        setSaving(true);
        try {
          const res = await onSave();
          if (res === false) return false; // le site garde la main (validation affichée)
        } catch (e) {
          toast.error(e && e.message ? e.message : "Échec de l'enregistrement : la fenêtre reste ouverte.");
          return false;
        } finally {
          setSaving(false);
        }
      }
      // Sortie animée, puis fermeture réelle. Sans animation (jsdom, clavier),
      // aucune attente : la fermeture reste synchrone, comme avant L5.
      const carte = cardRef.current;
      const sortie = carte ? jouerSortieFenetre(carte, mode, source) : null;
      if (sortie) await sortie;
      onRequestClose();
      if (sortie && carte && typeof requestAnimationFrame === "function") {
        // Filet : un parent qui garde la fenêtre montée la retrouve visible.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!monteRef.current) return;
          annulerAnimations(carte);
          if (carte.parentElement) annulerAnimations(carte.parentElement);
        }));
      }
      return true;
    } finally {
      askingRef.current = false;
    }
  }, [closable, confirm, dirty, onSave, closeMessage, onRequestClose, saving, mode]);

  // Expose requestClose aux ✕ custom du site.
  if (closeRef) closeRef.current = requestClose;
  // Le handler Échap lit toujours la DERNIÈRE version de requestClose via ce
  // ref : l'effet de pile peut ainsi être monté UNE SEULE fois. Sinon (effet
  // dépendant de [requestClose], recréé par les lambdas inline des sites),
  // chaque re-rendu du parent retirait puis re-poussait le token EN SOMMET de
  // pile — Échap pouvait alors viser la modale d'ARRIÈRE-PLAN d'un empilement.
  const requestCloseRef = useRef(null);
  requestCloseRef.current = requestClose;

  // Échap — seul le shell au sommet de la pile réagit. Monté une seule fois.
  useEffect(() => {
    const token = idRef.current;
    token.fermer = (source) => (requestCloseRef.current ? requestCloseRef.current(source) : Promise.resolve(false));
    _modalShellStack.push(token);
    monteRef.current = true;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (_modalShellStack[_modalShellStack.length - 1] !== token) return;
      e.stopPropagation();
      if (requestCloseRef.current) requestCloseRef.current("clavier");
    };
    document.addEventListener("keydown", onKey);
    return () => {
      monteRef.current = false;
      if (questionRef.current) { questionRef.current.abort(); questionRef.current = null; }
      document.removeEventListener("keydown", onKey);
      const i = _modalShellStack.indexOf(token);
      if (i >= 0) _modalShellStack.splice(i, 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feuille : geste sur la poignée et l'en-tête (jamais sur le contenu, qui
  // défile). Le geste DEMANDE la fermeture : garde intacte. Module chargé à
  // la demande (téléphone seulement), hors du bundle principal.
  useEffect(() => {
    if (!feuille) return undefined;
    let retrait = null;
    let fini = false;
    import("./fenetreGeste").then(({ brancherGesteFeuille }) => {
      if (fini || !cardRef.current) return;
      retrait = brancherGesteFeuille({
        carte: cardRef.current,
        zones: [poigneeRef.current, teteRef.current].filter(Boolean),
        surFermer: () => { if (requestCloseRef.current) requestCloseRef.current("geste"); },
        estReduit: mouvementReduit,
      });
    }).catch(() => { /* hors ligne : la feuille reste fermable par ✕, voile et Échap */ });
    return () => { fini = true; if (retrait) retrait(); };
  }, [feuille]);

  // Feuille : la bulle de messagerie flottante (z-index 9989, au-dessus des
  // fenêtres à 5000 par défaut) recouvrait le bas de la feuille, là où sont
  // ses boutons (relevé en recette L5). Elle s'efface tant qu'une feuille est
  // ouverte : <html data-feuille-ouverte> (règle dans CSS_PRIMITIVES).
  useEffect(() => {
    if (!feuille || typeof document === "undefined") return undefined;
    const racine = document.documentElement;
    _feuillesOuvertes += 1;
    racine.setAttribute("data-feuille-ouverte", "");
    return () => {
      _feuillesOuvertes = Math.max(0, _feuillesOuvertes - 1);
      if (!_feuillesOuvertes) racine.removeAttribute("data-feuille-ouverte");
    };
  }, [feuille]);

  // Feuille : le clavier virtuel d'iOS ne réduit pas la fenêtre de mise en
  // page ; sans ce relais, une feuille posée en bas passerait SOUS le clavier.
  // --fen-clavier = hauteur masquée par le clavier (0 sous 80 px : barres de
  // Safari), consommée par le rembourrage bas du voile.
  useEffect(() => {
    if (!feuille || typeof window === "undefined" || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const voile = cardRef.current && cardRef.current.parentElement;
    if (!voile) return undefined;
    const maj = () => {
      const masque = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      voile.style.setProperty("--fen-clavier", masque > 80 ? `${Math.round(masque)}px` : "0px");
    };
    maj();
    vv.addEventListener("resize", maj);
    vv.addEventListener("scroll", maj);
    return () => {
      vv.removeEventListener("resize", maj);
      vv.removeEventListener("scroll", maj);
    };
  }, [feuille]);

  // beforeunload quand dirty (fermeture d'onglet / refresh).
  useEffect(() => {
    if (!dirty || saving) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, saving]);

  // Focus initial non intrusif (n'écrase pas un autoFocus enfant).
  useEffect(() => {
    const t = setTimeout(() => {
      // preventScroll : posé pendant l'entrée de la fenêtre, le focus faisait
      // défiler le voile (overflow:hidden, sans retour possible) ; au téléphone
      // en paysage, le lecteur vidéo de la une s'ouvrait 120 px trop haut, sous
      // l'en-tête (relecture de L17.6, 04/10).
      if (initialFocusRef && initialFocusRef.current) { initialFocusRef.current.focus({ preventScroll: true }); return; }
      const carte = cardRef.current;
      if (!carte) return;
      // En portail, la fenêtre est le DERNIER enfant de <body> : laissé sur le
      // bouton qui l'a ouverte (clic dans Chrome, Entrée), le focus parcourait
      // toute la page sous le voile avant d'y entrer (relecture du 03/11/2026).
      // Elle le prend donc, sauf si un enfant l'a déjà (autoFocus).
      if (portail ? !carte.contains(document.activeElement) : document.activeElement === document.body) carte.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(t);
  }, [initialFocusRef, portail]);

  // En portail, le focus revient à fermeture au bouton qui a ouvert la fenêtre
  // (sinon il retombe sur <body>, en fin de page). Seulement s'il était dans
  // la fenêtre ou perdu : jamais repris à un champ que la page a focalisé.
  const ouvreurRef = useRef(null);
  if (portail && ouvreurRef.current === null && typeof document !== "undefined") {
    ouvreurRef.current = document.activeElement || false;
  }
  useEffect(() => {
    if (!portail) return undefined;
    const carte = cardRef.current;
    return () => {
      const ouvreur = ouvreurRef.current;
      if (!ouvreur || ouvreur === document.body || !ouvreur.isConnected || typeof ouvreur.focus !== "function") return;
      const actif = document.activeElement;
      if (!actif || actif === document.body || (carte && carte.contains(actif))) ouvreur.focus({ preventScroll: true });
    };
  }, [portail]);

  // ── Geometrie mobile (audit responsive iPhone, 10/09/2026) ────────────
  // Trois corrections liees, a ne pas dissocier :
  //  1. le calque defile dans les DEUX alignements. En align "center" il ne
  //     defilait pas : une carte plus haute que l'espace disponible etait
  //     rognee EN HAUT ET EN BAS, sans rattrapage — en paysage, le calque
  //     n'offre que ~212 px pour une carte de ~354, donc les boutons
  //     "Enregistrer"/"Annuler" sortaient de l'ecran.
  //  2. la carte est bornee en dvh (hauteur reellement visible) et non en
  //     vh, que iOS calcule barres masquees : 90vh depassait le visible.
  //     Par var(--vh100) (100dvh divise par le zoom de la taille du texte,
  //     03/10/2026) : un dvh brut sous ce zoom valait 1,24 ecran.
  //  3. margin:auto — seul patron qui centre un element trop grand TOUT EN
  //     laissant defiler vers le haut (alignItems:center, lui, rogne).
  // La zone sure basse evite que la barre d'accueil de l'iPhone recouvre
  // le dernier bouton de la fenetre.
  const overlayBase = {
    position: "fixed",
    top: "var(--app-header-h,56px)", left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,.5)",
    zIndex,
    display: "flex",
    alignItems: align === "top" ? "flex-start" : "center",
    justifyContent: "center",
    padding: "clamp(10px, var(--vw100) * 0.03, 20px)",
    overflowY: "auto",
    overscrollBehavior: "contain",
  };
  const cardBase = {
    background: "var(--bg-card, #fff)",
    borderRadius: "var(--radius-lg)", // valeur effective de l'ancien --r-md, retiré en L2 (L5 : échelle --radius-*)
    border: "1px solid var(--border-color)",
    maxWidth: maxWidth || (wide ? 720 : 560),
    width: "100%",
    maxHeight: align === "center" ? "min(var(--vh100) * 0.9, 100%)" : undefined,
    margin: align === "center" ? "auto" : undefined,
    overflowY: align === "center" ? "auto" : undefined,
    padding: "clamp(14px, var(--vw100) * 0.04, 20px)",
    boxSizing: "border-box",
  };

  // La zone sûre basse s'ajoute au rembourrage EFFECTIF du calque — celui de
  // l'appelant quand il en impose un (une bonne moitié des fenêtres passe un
  // `padding` par overlayStyle). L'ajouter en dur dans overlayBase le laissait
  // survivre à la surcharge : une fenêtre demandant 12 ou 16 px se retrouvait
  // avec 20 px en bas, au bureau aussi. Hors iPhone env(...) vaut 0 : le
  // rembourrage rendu est exactement celui d'avant.
  const overlayFusion = {
    // Géométrie plein écran reprise EXPLICITEMENT, bien qu'overlayBase la
    // repose juste après : le garde-fou src/modalOverlayLock.test.js reconnaît
    // un calque à ces littéraux, et une fusion par seul spread le rendait
    // invisible au scanner — la CI tombait alors que le comportement (clic
    // extérieur → dialogue de choix) n'avait pas bougé d'un iota.
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    ...overlayBase,
    ...(feuille ? sansCles(overlayStyle, DISPOSITION_VOILE) : (overlayStyle || {})),
  };
  if (feuille || panneau) {
    // Feuille : posée en bas, le voile ne défile plus (le corps de la feuille
    // défile) ; son rembourrage bas suit le clavier virtuel. Panneau : collé
    // au bord droit, sur toute la hauteur du voile.
    Object.assign(overlayFusion, feuille
      ? { display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "stretch",
          padding: 0, paddingBottom: "var(--fen-clavier, 0px)", overflow: "hidden" }
      : { justifyContent: "flex-end", alignItems: "stretch", padding: 0 });
  }
  const padBas = overlayFusion.paddingBottom ?? overlayFusion.padding;
  // Une valeur UNIQUE seulement : un raccourci à plusieurs valeurs
  // ("24px 12px") ne peut pas entrer dans un calc(). On neutralise les
  // parenthèses, imbriquées comprises, avant de chercher l'espace, sinon
  // clamp(a, var(--vw100) * k, c) passerait pour un raccourci ; une valeur qui
  // porte déjà la zone sûre basse ne la reçoit pas deux fois. (La feuille porte
  // elle-même sa zone sûre.)
  let sansParentheses = String(padBas);
  for (let avant = null; avant !== sansParentheses;) { avant = sansParentheses; sansParentheses = sansParentheses.replace(/\([^()]*\)/g, ""); }
  const estValeurUnique = !feuille && padBas != null
    && !sansParentheses.trim().includes(" ")
    && !/safe-area-inset-bottom|var\(--sab\b/.test(String(padBas));
  if (estValeurUnique) {
    const v = typeof padBas === "number" ? `${padBas}px` : String(padBas);
    overlayFusion.paddingBottom = `calc(${v} + env(safe-area-inset-bottom, 0px))`;
  }

  // Carte selon la présentation. Centre et tiroir : exactement la carte d'avant.
  let styleCarte;
  let styleCorps = null;
  if (feuille) {
    const aHauteur = !!cardStyle && (cardStyle.height !== undefined || cardStyle.minHeight !== undefined);
    // Calque ancré SOUS l'en-tête (top: var(--app-header-h…)) : la hauteur
    // mesurée de l'en-tête compte déjà l'encoche (son rembourrage --sat), la
    // feuille ne la retranche pas une seconde fois (PWA en portrait : ~59 px
    // de vide entre l'en-tête et la feuille, relecture du 03/10/2026). Elle
    // ne retranche que ce que le calque laisse de l'encoche : sans en-tête
    // (portail /espace-salarie, --app-header-h jamais posé), tout --sat.
    const sousEntete = /--app-header-h/.test(String(overlayFusion.top));
    const hauteurMax = sousEntete
      ? `calc(100% - 8px - max(0px, var(--sat, 0px) - ${overlayFusion.top}))`
      : "calc(100% - 8px - var(--sat, 0px))";
    styleCarte = {
      ...sansCles(cardStyle, [...GEOMETRIE_CARTE, ...DISPOSITION_CORPS, "position"]),
      background: (cardStyle && cardStyle.background) || "var(--bg-card)",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      maxWidth: "none",
      margin: 0,
      maxHeight: hauteurMax,
      height: aHauteur ? hauteurMax : undefined,
      borderRadius: "var(--radius-plaque, 20px) var(--radius-plaque, 20px) 0 0",
      borderBottom: "none",
      boxShadow: "var(--elev-2)",
      paddingBottom: "var(--sab, 0px)",
      // Zone sûre latérale : en paysage, l'îlot ou l'encoche couvre un bord ;
      // la croix de l'en-tête et le contenu restent à côté (iPhone, 03/10/2026).
      // Divisée par le zoom de la taille du texte (sous lui, 62 px en
      // rendaient 85,6 en Très grand, 04/10/2026).
      paddingLeft: "calc(var(--sal, 0px) / var(--fs-zoom, 1))",
      paddingRight: "calc(var(--sar, 0px) / var(--fs-zoom, 1))",
      boxSizing: "border-box",
      outline: "none",
    };
    styleCorps = {
      padding: "2px clamp(14px, var(--vw100) * 0.04, 20px) 20px",
      ...seulementCles(cardStyle, DISPOSITION_CORPS),
      // Sous un en-tête, le haut du corps est déjà aéré par la poignée et le titre.
      ...(title != null ? { paddingTop: 2 } : {}),
      flex: "1 1 auto",
      minHeight: 0,
      overflowY: (cardStyle && (cardStyle.overflowY || (cardStyle.overflow === "hidden" ? "hidden" : undefined))) || "auto",
      overscrollBehavior: "contain",
    };
  } else if (panneau) {
    styleCarte = {
      ...cardBase,
      ...sansCles(cardStyle, GEOMETRIE_CARTE),
      width: "100%",
      maxWidth: maxWidth || 420,
      height: "100%",
      maxHeight: "none",
      margin: 0,
      overflowY: "auto",
      borderRadius: "var(--radius-xl, 24px) 0 0 var(--radius-xl, 24px)",
      borderRight: "none",
      boxShadow: "var(--elev-2)",
      outline: "none",
    };
  } else {
    styleCarte = { ...cardBase, ...(cardStyle || {}), outline: "none" };
  }

  // Feuille : le titre s'aligne sur le rembourrage gauche du corps quand
  // l'appelant en donne un simple (padding: 22 ou paddingLeft).
  const margeTitre = !feuille ? undefined
    : typeof (cardStyle && cardStyle.paddingLeft) === "number" ? cardStyle.paddingLeft
    : typeof (cardStyle && cardStyle.padding) === "number" ? cardStyle.padding : "clamp(14px, var(--vw100) * 0.04, 20px)";
  const enTete = title != null && (
    <div ref={teteRef} className="fen-tete" style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 12, marginBottom: feuille ? 4 : 14, paddingLeft: margeTitre, paddingRight: feuille ? 8 : undefined }}>
      <div style={{ fontSize: "var(--fs-corps)", fontWeight: 600, color: "var(--text-main)",
        fontFamily: "Montserrat,sans-serif" }}>{title}</div>
      <button type="button" onClick={() => requestClose("bouton")} aria-label="Fermer"
        disabled={!isClosable || saving}
        style={{ border: "none", background: "transparent", cursor: "pointer",
          lineHeight: 1, color: "var(--text-sub)",
          minWidth: 44, minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center",
          opacity: (!isClosable || saving) ? 0.4 : 1 }}><Icone name="x" size={18} /></button>
    </div>
  );
  const attente = saving && (
    <div style={{ fontSize: "var(--fs-micro)", fontWeight: 700, color: "var(--text-sub)",
      marginBottom: 10 }}><IconeTexte nom="hourglass"/> Enregistrement…</div>
  );

  const fenetre = (
    <div
      className="fen-voile"
      data-fenetre={mode}
      style={overlayFusion}
      onClick={(e) => { if (e.target === e.currentTarget) requestClose("voile"); }}
    >
      <div
        ref={poserCarte}
        role="dialog"
        aria-modal="true"
        aria-label={!labelledBy && typeof title === "string" ? title : undefined}
        aria-labelledby={labelledBy || undefined}
        tabIndex={-1}
        className="fen-carte"
        data-mode={mode}
        style={styleCarte}
      >
        {feuille && <div ref={poigneeRef} className="fen-poignee" aria-hidden="true" />}
        {enTete}
        {feuille ? (
          <div className="fen-corps" style={styleCorps}>
            {attente}
            {children}
          </div>
        ) : (
          <>
            {attente}
            {children}
          </>
        )}
      </div>
    </div>
  );
  return portail && typeof document !== "undefined" && document.body ? createPortal(fenetre, document.body) : fenetre;
}

// ═══════════════════════════════════════════════════════════════════════════════
// EMPTY STATE avec CTA — remplace les "Rien à signaler" sans guidance
// Usage :
//   <EmptyStateCTA icon="🎉" title="Rien à voter"
//     message="Bravo, vous êtes à jour sur toutes les consultations."
//     actionLabel="Découvrir d'autres modules" onAction={() => onNav("athos")}/>
// ═══════════════════════════════════════════════════════════════════════════════

export function EmptyStateCTA({ icon = "🎉", title, message, actionLabel, onAction, compact = false }) {
  return (
    <div style={{
      padding: compact ? "14px 16px" : "22px 20px",
      textAlign: "center",
      color: "var(--text-sub)",
      fontSize: "var(--fs-micro)",
      lineHeight: 1.6,
    }}>
      {icon && <div style={{fontSize: compact ? "var(--fs-chronique)" : "var(--fs-page)", marginBottom: 6}}>{icon}</div>}
      {title && <div style={{fontWeight: 700, color: "var(--text-main)", marginBottom: 4,
        fontSize: compact ? "var(--fs-micro)" : "var(--fs-meta)"}}>{title}</div>}
      {message && <div style={{marginBottom: actionLabel ? 10 : 0}}>{message}</div>}
      {actionLabel && onAction && (
        <button onClick={onAction}
          style={{padding: "6px 14px", border: "none",
            background: C.orange, color: "var(--acc-navy-deep)",
            borderRadius: "var(--radius-sm)", cursor: "pointer",
            fontSize: "var(--fs-micro)", fontWeight: 700,
            marginTop: 4}}>
          {actionLabel} →
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM — palettes et composants visuels communs
// ═══════════════════════════════════════════════════════════════════════════════
// Principe directeur : chaque couleur porte une information.
//   • MODULE     → contexte de navigation (où suis-je dans Aramis)
//   • ENTITÉ     → à qui / quoi ça se rapporte (mutuelle, domaine)
//   • STATUT     → état actuel (géré par <StatusBadge/> — ne pas dupliquer ici)
// ═══════════════════════════════════════════════════════════════════════════════

// Couleurs par module (signature visuelle de chaque grande zone de l'app).
// Utilisée par <ModuleTopStripe/> (et <ModuleHero/> jusqu'au lot L4).
// Lot L3 (refonte « Mosaïque vivante », 27/09/2026) : DÉRIVÉE de la table
// unique NAV_COLORS (navConstants.js) — n'y recopiez plus aucun hex. Les clés
// historiques des appels <ModuleHero module="…"> sont des ALIAS du module de
// navigation. `teinte` = la teinte vive (liseré sous le header) ; `primary` /
// `secondary` = le fond de la bannière, à texte BLANC : la teinte si le blanc y
// tient, sinon son encre foncée (orange, bleu, sable, vert : fondBanniere).
// Lot L4 : plus aucune bannière ne lit primary / secondary (ModuleHero rend
// l'en-tête compact, les accueils la plaque navy) ; la table reste pour
// ModuleTopStripe (teinte) et pour les appelants extérieurs éventuels.
// Alias : ALIAS_MODULES, source unique dans navConstants.js.
const ALIAS_BANNIERE = ALIAS_MODULES;
const BANNIERE_NAVY = { primary: C.navy, secondary: "#14506e", gradientEnd: "#0a2a38" };
const banniere = (cle) => {
  const teinte = NAV_COLORS[cle];
  return cle === "pilotage"
    ? { ...BANNIERE_NAVY, teinte, icon: NAV_ICONES[cle] }
    : { primary: fondBanniere(teinte), secondary: encreSurBlanc(teinte), gradientEnd: C.navy, teinte, icon: NAV_ICONES[cle] };
};
const COCKPIT = { primary: fondBanniere(C.gold), secondary: encreSurBlanc(C.gold), gradientEnd: C.navy, teinte: C.gold, icon: "home" };
export const MODULE_COLORS = {
  cockpit: COCKPIT,
  "ma-semaine": COCKPIT,
  ...Object.fromEntries(Object.keys(NAV_COLORS).map((k) => [k, banniere(k)])),
  ...Object.fromEntries(Object.entries(ALIAS_BANNIERE).map(([alias, k]) => [alias, banniere(k)])),
  default: { ...BANNIERE_NAVY, teinte: C.navy, icon: "layout-grid" },
};

// ═══════════════════════════════════════════════════════════════════════════════
// <ModuleHero/> — en-tête d'une page de travail (27 appelants)
// Lot L4 (refonte « Mosaïque vivante ») : plus de bannière en dégradé à 135°,
// plus de filigrane ni de halo. Le rendu est l'en-tête COMPACT (EnTetePage.js) :
// fil « Enio / Module / Page », tuile pleine de 40 px, titre Light 32 px, ligne
// d'état, actions à droite. L'API ne change pas :
//   module   clé de navigation ou alias historique (MODULE_COLORS) ;
//   icon     clé de src/icones.js pour la tuile — un emoji est ignoré ;
//   accent   { primary } : teinte de la tuile (RH d'une mutuelle), le reste est
//            ignoré (plus de dégradé) ;
//   compact  sans effet : tous les en-têtes de page sont compacts.
// Les accueils de module ont la plaque navy (PlaqueModule.js), pas ce composant.
// Usage :
//   <ModuleHero module="athos" title="Projets & partenariats"
//     subtitle="128 projets actifs · 14 mutuelles engagées"
//     actions={<button className="btn btn-ghost btn-sm">Créer</button>}/>
// ═══════════════════════════════════════════════════════════════════════════════

export function ModuleHero({ module = "default", title, subtitle, icon, actions, children, accent = null }) {
  return (
    <EnTetePage module={module} titre={title} etat={subtitle} actions={actions}
      icone={icon} teinte={accent && accent.primary ? accent.primary : undefined}>
      {children}
    </EnTetePage>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// <ModuleTopStripe/> — fine bande de couleur (4px) en haut du module, signature
// visuelle discrète. À coller juste sous le header global.
// ═══════════════════════════════════════════════════════════════════════════════

export function ModuleTopStripe({ module = "default" }) {
  // Lot L9 : « Ma semaine » n'est pas un module, son liseré ne porte aucune
  // teinte (la bande reste, transparente : rien ne saute d'une page à
  // l'autre) ; plus de halo coloré sous le liseré (synthèse §4.4).
  const neutre = module === "cockpit" || module === "ma-semaine";
  const cfg = MODULE_COLORS[module] || MODULE_COLORS.default;
  return <div style={{ height: 3, background: neutre ? "transparent" : cfg.teinte }}/>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// <MutuelleChip/> — pastille colorée (avatar initiale + nom) avec la couleur
// officielle de la mutuelle. À utiliser dans les tableaux, listes, cartes.
// Usage :
//   <MutuelleChip name="Intériale" color="#f59d1f" />
//   <MutuelleChip name="Intériale" lists={lists} /> // couleur auto-lookup
// ═══════════════════════════════════════════════════════════════════════════════

export function MutuelleChip({ name, color, lists, size = "sm", onClick }) {
  if (!name) return null;
  // Lookup couleur si `lists` fourni et `color` absent
  let finalColor = color;
  if (!finalColor && lists) {
    const m = lists.find(l => (l.name || "").toLowerCase() === String(name).toLowerCase());
    finalColor = m?.color || C.gray;
  }
  finalColor = finalColor || C.gray;
  const initial = String(name).charAt(0).toUpperCase();
  const padH = size === "lg" ? 10 : size === "xs" ? 4 : 6;
  const padV = size === "lg" ? 4 : 2;
  const fontSize = size === "lg" ? 12 : size === "xs" ? 9 : 10;
  const avatarSize = size === "lg" ? 22 : size === "xs" ? 14 : 16;
  return (
    <span
      onClick={onClick}
      style={{
        display:"inline-flex", alignItems:"center", gap:5,
        padding:`${padV}px ${padH}px ${padV}px ${padV + 2}px`,
        background: alpha(finalColor, .082),
        border: `1px solid ${alpha(finalColor, .251)}`,
        borderRadius: "var(--radius-lg)",
        fontSize: taillePx(fontSize),
        fontWeight: 600,
        color: "var(--text-main)",
        cursor: onClick ? "pointer" : "default",
        whiteSpace: "nowrap",
        maxWidth: size === "lg" ? 180 : 140,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
      <span style={{
        width: avatarSize, height: avatarSize, borderRadius: "50%",
        background: `linear-gradient(135deg, ${finalColor} 0%, ${alpha(finalColor, .8)} 100%)`,
        color: "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: taillePx(fontSize - 1), fontWeight: 700, flexShrink: 0,
      }}>
        {initial}
      </span>
      <span style={{overflow:"hidden", textOverflow:"ellipsis"}}>{name}</span>
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// <AccentCard/> — card avec accent coloré (border-left + léger wash arrière-plan).
// À utiliser sur KPIs de cockpit, blocs suggestions, sections importantes.
// Usage :
//   <AccentCard color={C.teal} icon="⚡" title="Projets actifs" value="42"/>
//   <AccentCard color={C.orange}>{children}</AccentCard>
// ═══════════════════════════════════════════════════════════════════════════════

export function AccentCard({ color = C.orange, icon, title, value, subtitle, actionLabel, onAction, children, hover = true, compact = false }) {
  return (
    <div style={{
      background: "var(--bg-card, #fff)",
      // Un cadre (quatre côtés), pas un filet : la bordure posée en entier,
      // puis le liseré de gauche.
      border: "1px solid var(--border-color)",
      borderLeft: `4px solid ${color}`,
      borderRadius: "var(--radius-md)",
      padding: compact ? "10px 14px" : "14px 16px",
      position: "relative",
      overflow: "hidden",
      transition: hover ? "transform .15s, box-shadow .15s, border-color .15s" : "none",
    }}
    onMouseEnter={hover ? e => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = `0 6px 20px ${alpha(color, .133)}`;
    } : undefined}
    onMouseLeave={hover ? e => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "none";
    } : undefined}>
      {/* Wash subtil dans le coin supérieur */}
      <div style={{
        position:"absolute", top:0, right:0, width:80, height:80,
        background:`radial-gradient(circle at top right, ${alpha(color, .071)} 0%, transparent 70%)`,
        pointerEvents:"none",
      }}/>
      {(icon || title) && (
        <div style={{display:"flex", alignItems:"center", gap:8, marginBottom: value ? 4 : 6, position:"relative"}}>
          {icon && (
            <div style={{
              width: compact ? 24 : 28, height: compact ? 24 : 28, borderRadius: "var(--radius-sm)",
              background: alpha(color, .133), color: navTexte(color),
              display:"inline-flex", alignItems:"center", justifyContent:"center",
              fontSize: compact ? "var(--fs-nav)" : "var(--fs-corps)",
            }}>{icon}</div>
          )}
          {title && (
            <div style={{fontSize: "var(--fs-micro)", fontWeight: 700, color: navTexte(color)}}>
              {title}
            </div>
          )}
        </div>
      )}
      {value != null && (
        <div style={{fontSize: compact ? "var(--fs-chronique)" : "var(--fs-section)", fontWeight: 700, color: "var(--text-main)",
          lineHeight: 1.1, marginBottom: subtitle ? 4 : 0, fontFamily:"Montserrat,sans-serif"}}>
          {value}
        </div>
      )}
      {subtitle && (
        <div style={{fontSize: "var(--fs-micro)", color: "var(--text-sub)", lineHeight: 1.4}}>
          {subtitle}
        </div>
      )}
      {children}
      {actionLabel && onAction && (
        <button onClick={onAction}
          style={{marginTop:8, padding:"5px 10px", border:"none",
            background: alpha(color, .133), color: navTexte(color),
            borderRadius: "var(--radius-sm)", cursor: "pointer",
            fontSize: "var(--fs-micro)", fontWeight: 700}}>
          {actionLabel} →
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Animations CSS globales (.aramis-lift, .aramis-fade-in, .aramis-shimmer…) —
// DÉPLACÉES le 27/09/2026 (lot L2) dans la section « effets » de la feuille
// globale (src/feuilleGlobale.js, CSS_EFFETS). Ce <style> était rendu dans le
// <body> par Header.js : il passait donc APRÈS le socle mobile et n'existait
// qu'une fois l'en-tête monté. Le composant reste exporté (Header.js le rend
// encore) mais ne rend plus rien.
// ═══════════════════════════════════════════════════════════════════════════════

export function AramisGlobalStyles() {
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Palette domaines — utilisée par Dataviz et Athos pour cohérence visuelle
// (les couleurs officielles Enio de chaque domaine stratégique).
// ═══════════════════════════════════════════════════════════════════════════════

export const DOMAINE_COLORS_OFFICIAL = {
  "Développement & Fidélisation":        "#1D948A",
  "Développement et Fidélisation":       "#1D948A",
  "Affaires extérieures":                "#378ADD",
  "Affaires Extérieures":                "#378ADD",
  "Impact":                              "#5CB85C",
  "Pilotage éco. & réglementaire":       "#951B81",
  "Pilotage Économique et Réglementaire":"#951B81",
  "Ressources":                          "#F49800",
  "Direction générale":                  "#003F59",
  "Direction Générale":                  "#003F59",
  "DIRECTION GÉNÉRALE":                  "#003F59",
};

// ═══════════════════════════════════════════════════════════════════════════════
// Visibilité des réunions — règle unique appliquée partout
// ═══════════════════════════════════════════════════════════════════════════════
// Une réunion est visible par un utilisateur si :
//   1. L'utilisateur est administrateur (voit tout),
//   2. OU la réunion est marquée publique (`publique: true`),
//   3. OU l'utilisateur est dans `invites[]` (par id ou email).
//
// NB : auparavant, les rôles de gouvernance (DG/PR/CA/CD) avaient un bypass
// "hasPrivilegedRole" qui leur faisait voir toutes les réunions, toutes mutuelles
// confondues. Ce bypass a été retiré suite à un incident : un DG d'une mutuelle
// voyait les CODIR d'une autre mutuelle. Les dirigeants n'ont désormais plus de
// privilège spécial en visibilité.
// ═══════════════════════════════════════════════════════════════════════════════

export function canViewReunion(reunion, session, opts = {}) {
  if (!reunion) return false;
  if (opts.isAdmin) return true;
  if (reunion.publique === true) return true;
  const userId = session?.id;
  const email = (session?.email || "").toLowerCase();
  const invites = reunion.invites || [];
  // Matching par id OU email (lowercase). L'invite.id peut être un UUID propre
  // à l'invitation, donc le fallback email est indispensable.
  return invites.some(i => {
    if (i.id && userId && i.id === userId) return true;
    const iEmail = (i.email || "").toLowerCase();
    return email && iEmail && iEmail === email;
  });
}

// ── Module RH/Recrutement : qui peut accéder à la banque de CV, aux offres
// d'emploi mutualisées et au matching IA ?
//   • Admin Enio (toujours)
//   • Rôle « RH » coché sur la fiche, ou DG
// Décision de Pierre du 15/10/2026 : ni le CODIR, ni le pôle d'intérêt
// « Ressources humaines ». Cette copie oubliait par ailleurs le rôle « RH »
// lui-même — d'où la source unique src/droitsRH.js, à laquelle elle délègue.
// CV = données très sensibles → permission stricte, journalisation côté CF.
export function canAccessRH(session, { isAdmin } = {}) {
  return peutAccederRH(session, { isAdmin });
}

// ── Modification de statut d'engagement : qui peut soumettre une demande ?
//   • Admin Enio (toujours)
//   • CD (CODIR), DG, PR (Président)
//   • Délégué porteur du mandat ENG (Mandataire pour les engagements)
// Passer ici le doc delegate fraîchement chargé depuis Firestore (delegates),
// pas aramisSession qui peut être stale.
export function canRequestEngagementModification(member, { isAdmin } = {}) {
  if (isAdmin) return true;
  if (!member) return false;
  if (member.mandatENG === true) return true;
  const roles = Array.isArray(member.roles) ? member.roles : [];
  return roles.includes("CD") || roles.includes("DG") || roles.includes("PR");
}

// Demande d'inscription à une réunion publique : ajoute une entrée dans
// reunion.joinRequests[]. Retourne la liste mise à jour.
export function addJoinRequest(reunion, session) {
  const list = Array.isArray(reunion.joinRequests) ? reunion.joinRequests : [];
  // Idempotent : si une demande pending existe déjà pour cet user, on ne duplique pas
  const existing = list.find(r => r.userId === session?.id && r.status === "pending");
  if (existing) return { list, alreadyRequested: true };
  return {
    list: [
      ...list,
      {
        userId: session?.id || "",
        userPrenom: session?.prenom || "",
        userNom: session?.nom || "",
        userEmail: session?.email || "",
        userMutuelle: session?.mutuelle || "",
        ts: Date.now(),
        status: "pending",
      },
    ],
    alreadyRequested: false,
  };
}

// Décision admin sur une demande d'inscription : maj status + (si approuvée)
// ajout dans invites[] pour que la personne voie la réunion.
export function decideJoinRequest(reunion, requestUserId, decision, adminName) {
  const list = Array.isArray(reunion.joinRequests) ? reunion.joinRequests : [];
  const idx = list.findIndex(r => r.userId === requestUserId && r.status === "pending");
  if (idx === -1) return null;
  const req = list[idx];
  const newList = [...list];
  newList[idx] = {
    ...req,
    status: decision,                  // "approuvee" | "refusee"
    decidePar: adminName || "Admin",
    decideLe: Date.now(),
  };
  const invitesBase = reunion.invites || [];
  let newInvites = invitesBase;
  if (decision === "approuvee") {
    // Ajouter dans invites si pas déjà présent
    const already = invitesBase.some(i => i.id === req.userId || (i.email || "").toLowerCase() === (req.userEmail || "").toLowerCase());
    if (!already) {
      newInvites = [
        ...invitesBase,
        {
          id: req.userId,
          prenom: req.userPrenom,
          nom: req.userNom,
          email: req.userEmail,
          mutuelle: req.userMutuelle,
          statut: "en_attente",
          ajouteVia: "join_request",
          ajouteLe: Date.now(),
        },
      ];
    }
  }
  return { joinRequests: newList, invites: newInvites };
}

// Palette ordonnée pour graphiques multi-séries (n couleurs distinctes).
// À utiliser dans Dataviz au lieu de palette bleu/orange monotone.
export const CHART_PALETTE = [
  "#1D948A", // teal
  "#F49800", // orange Enio
  "#951B81", // violet
  "#378ADD", // bleu
  "#5CB85C", // vert
  "#F49800", // gold
  "#E20816", // rouge
  "#003F59", // navy
  "#7c4dbb", // violet clair
  "#FF6B6B", // rose
];

// ═══════════════════════════════════════════════════════════════════════════════
// <Skeleton/> — placeholder shimmer pendant le chargement des données
// Usage :
//   {loading ? <Skeleton lines={3}/> : <RealContent/>}
//   <Skeleton w={200} h={20}/>  // dimensions explicites
// ═══════════════════════════════════════════════════════════════════════════════

export function Skeleton({ w, h = 14, lines = 1, gap = 6, circle = false, style = {} }) {
  if (circle) {
    return (
      <div className="aramis-shimmer" style={{
        width: w || h, height: h, borderRadius: "50%",
        display: "inline-block", ...style,
      }}/>
    );
  }
  if (lines > 1) {
    return (
      <div style={{display:"flex", flexDirection:"column", gap}}>
        {Array.from({length: lines}).map((_, i) => (
          <div key={i} className="aramis-shimmer" style={{
            width: i === lines - 1 ? "60%" : (w || "100%"),
            height: h, borderRadius: "var(--radius-xs)",
          }}/>
        ))}
      </div>
    );
  }
  return (
    <div className="aramis-shimmer" style={{
      width: w || "100%", height: h, borderRadius: "var(--radius-xs)",
      display: "block", ...style,
    }}/>
  );
}

// Card-skeleton pré-dimensionnée (remplace l'attente d'une liste d'items)
export function SkeletonCard({ count = 3 }) {
  return (
    <div style={{display:"flex", flexDirection:"column", gap:8}}>
      {Array.from({length:count}).map((_, i) => (
        <div key={i} style={{
          padding: "14px 16px", borderRadius: "var(--radius-md)",
          background: "var(--bg-card, #fff)",
          border: "1px solid var(--border-color)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <Skeleton circle h={36}/>
          <div style={{flex:1}}>
            <Skeleton w="45%" h={13} style={{marginBottom:6}}/>
            <Skeleton w="75%" h={10}/>
          </div>
          <Skeleton w={60} h={22} style={{borderRadius: "var(--radius-md)"}}/>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// <CountUpNumber/> — DEPUIS L5 (28/09/2026) le chiffre ROULE depuis sa valeur
// précédente (moment M5, src/Odometre.js) au lieu de recompter depuis 0 à
// chaque montage et à chaque onSnapshot. Nom et props gardés (value, format,
// prefix, suffix, className ; duration est ignorée). Pour du code neuf :
// <Odometre valeur={…} />, importé de src/Odometre.js.
// ═══════════════════════════════════════════════════════════════════════════════
export { CountUpNumber } from "./Odometre";

// ═══════════════════════════════════════════════════════════════════════════════
// <Sparkline/> — mini-graphique inline de tendance (SVG léger)
// Usage : <Sparkline data={[1,3,2,5,4,6,8]} color={C.teal}/>
// ═══════════════════════════════════════════════════════════════════════════════

export function Sparkline({ data = [], color = C.teal, width = 60, height = 18, strokeWidth = 1.5, area = true }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - strokeWidth * 2) - strokeWidth;
    return [x, y];
  });
  const pathD = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L${width},${height} L0,${height} Z`;
  const last = points[points.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      style={{verticalAlign: "middle", overflow: "visible"}}>
      {area && (
        <path d={areaD} fill={color} fillOpacity={0.15}/>
      )}
      <path d={pathD} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={last[0]} cy={last[1]} r={strokeWidth + 1}
        fill={color} stroke="var(--bg-card, #fff)" strokeWidth={1}/>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// <RoleHalo/> — enveloppe un avatar (ou n'importe quel enfant) d'un halo coloré
// selon le rôle gouvernance. DG=gold, PR=orange, CA=teal, CD=violet, AG=bleu.
// Usage :
//   <RoleHalo role="DG"><img src="..."/></RoleHalo>
//   <RoleHalo roles={["DG","CA"]}><Avatar/></RoleHalo>  // prend le premier rôle prioritaire
// ═══════════════════════════════════════════════════════════════════════════════

const ROLE_COLORS = {
  DG: C.gold,
  PR: C.orange,
  CD: C.violet,
  CA: C.teal,
  AG: C.blue,
};
// Priorité d'affichage : on n'affiche qu'un seul halo (le plus "haut")
const ROLE_PRIORITY = ["DG", "PR", "CD", "CA", "AG"];

export function roleOfDelegate(d) {
  if (!d) return null;
  const roles = d.roles || [];
  return ROLE_PRIORITY.find(r => roles.includes(r)) || null;
}

export function roleColor(role) {
  return ROLE_COLORS[role] || null;
}

export function RoleHalo({ role, roles, children, size = 0, pulse = false, style = {} }) {
  const r = role || (roles ? ROLE_PRIORITY.find(x => roles.includes(x)) : null);
  const color = ROLE_COLORS[r];
  if (!color) return <>{children}</>;
  return (
    <div style={{
      display: "inline-block", position: "relative",
      padding: size, borderRadius: "50%",
      boxShadow: `0 0 0 2px ${color}, 0 0 0 ${size + 3}px ${alpha(color, .2)}`,
      animation: pulse ? "aramisPulse 2.5s ease-in-out infinite" : undefined,
      ...style,
    }} title={r ? "Rôle : " + r : ""}>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// <ProgressRing/> — anneau circulaire de progression (complétude du profil)
// Usage : <ProgressRing value={67} size={48} color={C.gold}/>  // 0-100
// ═══════════════════════════════════════════════════════════════════════════════

export function ProgressRing({ value = 0, size = 48, strokeWidth = 4, color = C.orange, bg = "var(--border-color)", label, showPct = true, children }) {
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const dashOffset = circumference - (pct / 100) * circumference;
  return (
    <div style={{display:"inline-block", position:"relative", width: size, height: size}}>
      <svg width={size} height={size} style={{transform: "rotate(-90deg)"}}>
        <circle cx={size/2} cy={size/2} r={r}
          fill="none" stroke={bg} strokeWidth={strokeWidth}/>
        <circle cx={size/2} cy={size/2} r={r}
          fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{transition: "stroke-dashoffset .6s cubic-bezier(.2,.8,.2,1)"}}/>
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column",
        fontSize: size > 50 ? "var(--fs-meta)" : "var(--fs-micro)", fontWeight: 700,
        color: "var(--text-main)",
        pointerEvents: "none",
      }}>
        {children || (showPct && pourcentFr(Math.round(pct), { decimales: 0 }))}
      </div>
      {label && <div style={{fontSize:"var(--fs-micro)", color:"var(--text-sub)", textAlign:"center",
        marginTop:4, fontWeight:600}}>{label}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TexteAvecMentions — affiche un texte en stylisant les @Prénom Nom
// (refonte 2026-05-08). Utilisé dans Questions & entraide.
//
// Le parsing est tolérant aux accents (NFD). Une mention n'est mise en
// évidence QUE si le @Prénom Nom correspond à un délégué de la liste —
// les @autres sont laissés tels quels.
// ═══════════════════════════════════════════════════════════════════════════════

const _normMention = (s) => String(s || "")
  .normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export function TexteAvecMentions({ texte, delegates = [], style = {}, mentionColor = "#1D948A" }) {
  if (!texte) return null;
  // Build name index : "prenom nom" (norm) → delegate
  const byNorm = new Map();
  for (const d of (delegates || [])) {
    const k = _normMention(`${d.prenom || ""} ${d.nom || ""}`);
    if (k.length >= 3) byNorm.set(k, d);
  }
  // Regex : @ suivi d'au moins 2 mots de lettres (gère prénoms composés simples).
  // \p{L} = lettre Unicode, \p{M} = combining marks (accents)
  const regex = /@([\p{L}\p{M}'-]+(?:\s[\p{L}\p{M}'-]+)+)/gu;
  const tokens = [];
  let lastIdx = 0;
  let match;
  while ((match = regex.exec(texte)) !== null) {
    const full = match[1];
    const norm = _normMention(full);
    const delegate = byNorm.get(norm);
    if (delegate) {
      if (match.index > lastIdx) {
        tokens.push({ type: "text", value: texte.slice(lastIdx, match.index) });
      }
      tokens.push({ type: "mention", value: "@" + full, delegate });
      lastIdx = match.index + match[0].length;
    }
  }
  if (lastIdx < texte.length) {
    tokens.push({ type: "text", value: texte.slice(lastIdx) });
  }
  // Si aucune mention détectée, rendu direct (préserve sauts de ligne via white-space)
  if (tokens.length === 0) tokens.push({ type: "text", value: texte });

  return (
    <span style={{ whiteSpace: "pre-wrap", ...style }}>
      {tokens.map((t, i) =>
        t.type === "mention" ? (
          <span key={i} style={{
            background: alpha(mentionColor, .102),
            color: navTexte(mentionColor),
            padding: "1px 6px",
            borderRadius: "var(--radius-sm)",
            fontWeight: 700,
            fontSize: "0.95em",
          }} title={`Mention de ${t.delegate.prenom || ""} ${t.delegate.nom || ""}${t.delegate.mutuelle ? " — " + t.delegate.mutuelle : ""}`}>
            {t.value}
          </span>
        ) : (
          <span key={i}>{t.value}</span>
        )
      )}
    </span>
  );
}
